import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { quoteTracker } from '../lib/consultSource';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SavedConfigsPage = () => {
  const { token, isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchConfigs();
    }
  }, [isAuthenticated]);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/saved-configs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(response.data.configs || response.data || []);
    } catch (error) {
      console.error('Failed to fetch configs:', error);
      toast.error('Failed to load saved designs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId) => {
    try {
      await axios.delete(`${API_URL}/user/saved-configs/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(configs.filter(c => c.id !== configId));
      toast.success('Design deleted');
    } catch (error) {
      toast.error('Failed to delete design');
    }
  };

  const handleAddToCart = async (config) => {
    try {
      await addToCart(config);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const getProductName = (type) => {
    const names = { 'roller-shades': 'Roller Shade', 'motorized-drapes': 'Motorized Drapes', 'roman-shades': 'Roman Shade' };
    return names[type] || 'Custom Product';
  };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-6 pt-32">
          <div className="text-center">
            <p className="text-neutral-500 mb-4">Please sign in to view saved designs</p>
            <Button onClick={() => navigate('/login')} className="btn-primary">Sign In</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen pt-32 pb-20" data-testid="saved-configs-page">
        <div className="container-xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="label-overline text-amber-700">My Account</span>
              <h1 className="mt-2 text-neutral-900">Saved Designs</h1>
            </div>
            <Button onClick={() => navigate('/products')} className="btn-primary">Create New</Button>
          </div>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map(i => <div key={i} className="h-48 bg-neutral-100 animate-pulse" />)}
            </div>
          ) : configs.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-500 mb-4">No saved designs yet</p>
              <Button onClick={() => navigate('/products')} className="btn-primary">Start Designing</Button>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {configs.map((config, index) => (
                <motion.div key={config.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="border border-neutral-200 p-6" data-testid={`saved-config-${config.id}`}>
                  <div className="flex gap-4 mb-4">
                    <div className="w-16 h-16 bg-neutral-100 flex-shrink-0 flex items-center justify-center text-xs text-neutral-500">
                      {config.width}"×{config.height}"
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-neutral-900">{config.room_label || getProductName(config.product_type)}</p>
                      <p className="text-sm text-neutral-500">{getProductName(config.product_type)}</p>
                      <p className="text-sm text-neutral-400 font-mono">{config.width}" W × {config.height}" H</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                    <p className="font-mono text-lg font-medium">{formatPrice(config.total_price)}</p>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" onClick={() => handleAddToCart(config)} className="gap-2">
                        <ShoppingBag className="w-4 h-4" /> Add to Quote
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export const QuotesPage = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuotes();
    }
  }, [isAuthenticated]);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(response.data);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${quoteId}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apollo-quote-${quoteId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-6 pt-32">
          <div className="text-center">
            <p className="text-neutral-500 mb-4">Please sign in to view your quotes</p>
            <Button onClick={() => navigate('/login')} className="btn-primary">Sign In</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen pt-32 pb-20" data-testid="quotes-page">
        <div className="container-xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="label-overline text-amber-700">My Account</span>
              <h1 className="mt-2 text-neutral-900">My Quotes</h1>
            </div>
            <Button onClick={() => { quoteTracker.mark('Saved quotes: New Quote'); navigate('/quote'); }} className="btn-primary">New Quote</Button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="h-20 bg-neutral-100 animate-pulse" />)}
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-neutral-500 mb-4">No quotes yet</p>
              <Button onClick={() => navigate('/products')} className="btn-primary">Start Designing</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {quotes.map((quote, index) => (
                <motion.div key={quote.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="border border-neutral-200 p-6" data-testid={`quote-${quote.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-mono text-lg font-medium">#{quote.id.slice(0, 8).toUpperCase()}</p>
                        <span className={`px-2 py-0.5 text-xs ${quote.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{quote.status}</span>
                      </div>
                      <p className="text-sm text-neutral-500">{quote.items?.length || 0} items • {new Date(quote.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="font-mono text-xl font-medium">{formatPrice(quote.total)}</p>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(quote.id)}>Download PDF</Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
